import type {
  Active,
  DragEndEvent,
  DragStartEvent,
  UniqueIdentifier,
} from '@dnd-kit/core'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import type { ReactNode } from 'react'
import { Fragment, useCallback, useMemo, useState } from 'react'
import { CrewSortableUnorderedListItem } from './components/crewSortableUnorderedListItem/crewSortableUnorderedListItem'
import { CrewDragHandleForUnorderedList } from './components/crewDragHandle/crewDragHandle'
import { CrewSortableOverlay } from '../crewSortableList/components/crewSortableOverlay/crewSortableOverlay'

export type CrewSortableUnorderedListBaseItem = {
  id: UniqueIdentifier
}

export { arrayMove }

type CrewSortableUnorderedListProps<T> = {
  items: T[]
  onReorder(reorderingEntityId: string, targetPositionEntityId: string): void
  renderItem(item: T): ReactNode
  renderOverlayItem(item: T): ReactNode
  className?: string
}

export const CrewSortableUnorderedList = <
  T extends CrewSortableUnorderedListBaseItem
>({
  items,
  onReorder,
  renderItem,
  renderOverlayItem,
  className = '',
}: CrewSortableUnorderedListProps<T>) => {
  const [active, setActive] = useState<Active | null>(null)

  // Get dragging item from the list
  const activeItem = useMemo(
    () => items.find((item) => item.id === active?.id),
    [active, items]
  )

  // Detect different input methods in order to initiate drag operations, respond to movement and end or cancel the operation.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10, // 10px移動してからドラッグ開始（少し大きめに設定）
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Handle when drag a specific item
  const handleDragStart = useCallback(({ active }: DragStartEvent) => {
    // Update dragging item
    setActive(active)
  }, [])

  // Handle when drop to another specific item
  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      // Update dragging item to nothing
      setActive(null)

      if (over == null || active.id === over.id) {
        // 順番入れ替え無し
        return
      }

      if (typeof active.id !== 'string' || typeof over.id !== 'string') {
        // ここでidがstringでないのは実装ミス
        throw new Error('Invalid id type. Expected string.')
      }

      onReorder(active.id, over.id)
    },
    [onReorder]
  )

  // Handle when doesn't drop to another specific item
  const handleDragCancel = useCallback(() => {
    setActive(null)
  }, [])

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <ul className={className}>
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          {items.map((item) => (
            <Fragment key={item.id}>{renderItem(item)}</Fragment>
          ))}
        </SortableContext>
      </ul>
      <CrewSortableOverlay>
        {activeItem ? renderOverlayItem(activeItem) : null}
      </CrewSortableOverlay>
    </DndContext>
  )
}

CrewSortableUnorderedList.Item = CrewSortableUnorderedListItem
CrewSortableUnorderedList.DragHandle = CrewDragHandleForUnorderedList
